import {
  useAssignSettingNewEmailUnsubscribePreferencePage,
  useSettingNewEmailUnsubscribePreferencePage,
} from "@faire/retailer-visitor-shared/settings/useSettingNewEmailUnsubscribePreferencePage";
import { BrandSentEmailPreferences } from "@faire/retailer-visitor-shared/views/settings/email-preferences/components/BrandSentEmailPreferences";
import { FaireSentEmailPreferences } from "@faire/retailer-visitor-shared/views/settings/email-preferences/components/FaireSentEmailPreferences";
import { useEmailPreferencesQuery } from "@faire/retailer-visitor-shared/views/settings/email-preferences/hooks/useEmailPreferencesQuery";
import { CONTENT_PADDING_LEFT } from "@faire/retailer-visitor-shared/views/settings/Layout";
import { SettingsSectionHeader } from "@faire/retailer-visitor-shared/views/settings/SettingsSectionHeader";
import { exhaustiveSwitchWithDefault } from "@faire/web--source/common/exhaustiveSwitchWithDefault";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { Path } from "@faire/web--source/common/Path";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Tabs } from "@faire/web--source/slate/Tabs";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackRetailerSettingsPageEmailPreferencesTabView } from "@faire/web-api--source/events/retailerSettingsPage/view/emailPreferencesTab";
import { QueryParameters as EmailPreferencesPageQueryParams } from "@faire/web-api--source/routes/www/retailer/settings/email-preferences";
import * as React from "react";
import { useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

enum EmailPreferencesTab {
  FAIRE_SENT = "FAIRE_SENT",
  BRAND_SENT = "BRAND_SENT",
}

export const EmailPreferencesForm: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const history = useHistory();
  const tabs = useEmailPreferencesTabs();
  const settingNewEmailUnsubscribePreferencePage =
    useSettingNewEmailUnsubscribePreferencePage();
  const { tab } = useQueryParams(EmailPreferencesPageQueryParams.parse);
  const assignSettingNewEmailUnsubscribePreferencePage =
    useAssignSettingNewEmailUnsubscribePreferencePage();
  const { emailPreferencesResponse } = useEmailPreferencesQuery();
  const emailPreferencesTitleRef = useRef<HTMLDivElement>(null);

  const handleTabChange = React.useCallback(
    (newTab: EmailPreferencesTab) => {
      const path = new Path(history.location);
      path.replace({ tab: newTab });
      if (newTab === EmailPreferencesTab.FAIRE_SENT) {
        // page param is specific to the brand-sent email tab, remove if navigating to faire-sent
        path.delete("page");
      }
      history.push(path.toString());
    },
    [history]
  );

  const scrollToTopOfTable = useCallback(() => {
    emailPreferencesTitleRef.current?.scrollIntoView();
  }, []);

  useEffect(() => {
    assignSettingNewEmailUnsubscribePreferencePage();
  }, [assignSettingNewEmailUnsubscribePreferencePage]);

  const activeTab =
    EmailPreferencesTab[tab as keyof typeof EmailPreferencesTab] ??
    EmailPreferencesTab.FAIRE_SENT;

  return (
    <FullWidthContainer>
      <EmailPreferencesContentWrapper ref={emailPreferencesTitleRef}>
        <SettingsSectionHeader
          title={strictLocalize({
            defaultMessage: "Email preferences",
            description: {
              text: "Title on page that allows user to configure what types of emails they want to receive.",
              img: "https://cdn.faire.com/fastly/6ab8cc2c60fb4e6a9b47ee5c2952b11d53e3e1604aafe39b7d587685ae31f970.png",
            },
          })}
          description={
            <StrictLocalMsg
              defaultMessage="Manage your email preferences for {email_address_to_display}"
              values={DEPRECATED_backwardsCompatibleValues({
                email_address_to_display:
                  emailPreferencesResponse?.email_address_to_display,
              })}
            />
          }
        />
      </EmailPreferencesContentWrapper>
      {settingNewEmailUnsubscribePreferencePage ? (
        <>
          <Spacer height={4} />
          <StyledTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
          <Spacer height="4x" />
          <TabContent
            activeTab={activeTab}
            scrollToTopOfTable={scrollToTopOfTable}
          />
        </>
      ) : (
        <>
          <Spacer height={28} />
          <FaireSentEmailPreferences />
        </>
      )}
    </FullWidthContainer>
  );
};

const TabContent: React.FC<{
  activeTab: EmailPreferencesTab;
  scrollToTopOfTable: () => void;
}> = ({ activeTab, scrollToTopOfTable }) => {
  useEffect(() => {
    trackRetailerSettingsPageEmailPreferencesTabView(activeTab);
  }, [activeTab]);

  switch (activeTab) {
    case EmailPreferencesTab.FAIRE_SENT:
      return <FaireSentEmailPreferences />;
    case EmailPreferencesTab.BRAND_SENT:
      return (
        <BrandSentEmailPreferences scrollToTopOfTable={scrollToTopOfTable} />
      );
    default:
      return exhaustiveSwitchWithDefault(null)(activeTab);
  }
};

const useEmailPreferencesTabs = () => {
  const { strictLocalize } = useStrictLocalization();

  return [
    {
      value: EmailPreferencesTab.FAIRE_SENT,
      label: strictLocalize({
        defaultMessage: "Emails from Faire",
        description: {
          text: "Label on a tab containing email preferences for emails sent from Faire.",
          img: "https://cdn.faire.com/fastly/00cea30090750026f8761c9878d7861fdf5f849d9435cd39bae560736d10ecd7.png",
        },
      }),
    },
    {
      value: EmailPreferencesTab.BRAND_SENT,
      label: strictLocalize({
        defaultMessage: "Emails from brands",
        description: {
          text: "Label on a tab containing email preferences for emails sent from brands.",
          img: "https://cdn.faire.com/fastly/00cea30090750026f8761c9878d7861fdf5f849d9435cd39bae560736d10ecd7.png",
        },
      }),
    },
  ];
};

const FullWidthContainer = styled(Column)`
  width: 100%;
`;

export const EmailPreferencesContentWrapper = styled.section`
  max-width: 800px;
  width: 100%;
`;

const StyledTabs = styled(Tabs<EmailPreferencesTab>)`
  ${responsiveMediaQueries.tabletAndAbove} {
    position: relative;
    left: -${CONTENT_PADDING_LEFT};

    > div > div:first-child {
      padding-left: ${CONTENT_PADDING_LEFT};
    }
  }
`;
